import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/apiClient';
import type { DraftEnvelope, SchemaMap } from '@/types/sections';
import { useSelectionStore } from '@/state/selectionStore';

const SCHEMAS_KEY = ['sections', 'schemas'] as const;
const TEMPLATES_KEY = ['sections', 'templates'] as const;

export interface TemplateListEntry {
  template: string;
  label: string;
  kind: 'base' | 'override';
  parent?: string;
  handle?: string;
}

export function useTemplateList() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () =>
      apiRequest<{ templates: TemplateListEntry[] }>('/api/sections/templates'),
    staleTime: 60 * 1000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { base: string; handle: string }) =>
      apiRequest<{ template: string }>('/api/sections/templates', {
        method: 'POST',
        body: args,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useSectionsDraft() {
  const template = useSelectionStore((s) => s.template);
  return useQuery({
    queryKey: ['sections', 'draft', template],
    queryFn: () =>
      apiRequest<DraftEnvelope>(
        `/api/sections/draft?template=${encodeURIComponent(template)}`,
      ),
    // Manual-control: the editing session is the only writer. No background
    // refetch may clobber the staged copy / undo history — post-save
    // setQueryData injects the saved blob as the cache value.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useSectionSchemas() {
  return useQuery({
    queryKey: SCHEMAS_KEY,
    queryFn: () => apiRequest<SchemaMap>('/api/sections/schemas'),
    staleTime: 5 * 60 * 1000,
  });
}
